// External libraries
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import cx from 'classnames';
import Scroll from 'react-scroll';
import valid from 'card-validator';
import get from 'lodash/get';
import { Map, Set, OrderedMap } from 'immutable';

// Internal libraries
import { paymentTokenizationFailureMessage } from 'lib/common/messages';
import UserModel from 'models/user';
import NewPaymentMethodModel from 'models/braintree/new-payment-method';
import PaymentMethod from 'models/payment-method';

import { dataCollectorShape, braintreeClientInstanceShape, paypalInstanceShape } from 'lib/common/prop-types';

// Components
import PaymentMethodForm from 'components/payment-methods/payment-method-form';
import ProgressButton from 'components/common/buttons/progress-button';

import changePaymentMethod from 'action-creators/payment-methods/change-payment-method';
import toggleSavePayment from 'action-creators/checkout/toggle-save-payment';
import modifyRequiredFieldListCreator from 'action-creators/payment-methods/modify-required-field-list';
import braintreeFieldStateChange from 'action-creators/payment-methods/braintree-field-state-change';
import setShouldScrollToError from 'action-creators/payment-methods/set-should-scroll-to-error';
import addMessageAndScrollToTop from 'action-creators/messaging/add-message-and-scroll-to-top';
import handlePaymentError from 'action-creators/payment-methods/handle-payment-error';
import trackEventCreator from 'action-creators/analytics/track-event';

const BRAINTREE_FIELDS = ['number', 'expirationMonth', 'expirationYear', 'cvv', 'postalCode'];

const propTypes = {
  saveCreditCard: PropTypes.bool.isRequired,
  user: PropTypes.instanceOf(UserModel).isRequired,
  isPaypal: PropTypes.bool.isRequired,
  newPaymentMethod: PropTypes.instanceOf(NewPaymentMethodModel).isRequired,
  requiredFields: PropTypes.instanceOf(Set),
  requiredFieldErrors: PropTypes.oneOfType([
    PropTypes.instanceOf(Map),
    PropTypes.instanceOf(OrderedMap),
  ]).isRequired,
  braintreeToken: PropTypes.string,
  shouldScrollToError: PropTypes.bool,
  addMessageAndScrollToTop: PropTypes.func.isRequired,
  toggleSavePayment: PropTypes.func.isRequired,
  modifyRequiredFieldList: PropTypes.func.isRequired,
  changePaymentMethod: PropTypes.func.isRequired,
  setShouldScrollToError: PropTypes.func,
  braintreeFieldStateChange: PropTypes.func.isRequired,
  submitCheckout: PropTypes.func.isRequired,
  handlePaymentError: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
  braintreeClientInstance: braintreeClientInstanceShape,
  paypalInstance: paypalInstanceShape,
  dataCollector: dataCollectorShape,
  submitButton: PropTypes.instanceOf(ProgressButton),
  checkoutTotal: PropTypes.number,
  selectedPaymentMethod: PropTypes.instanceOf(PaymentMethod),
};

const defaultProps = {
  shouldScrollToError: false,
  requiredFields: Set(),
  braintreeToken: null,
  setShouldScrollToError: () => {},
  braintreeClientInstance: null,
  paypalInstance: null,
  dataCollector: null,
  submitButton: null,
  checkoutTotal: null,
  selectedPaymentMethod: null,
};

class NewPaymentMethod extends Component {
  constructor(props) {
    super(props);
    this.onFieldEvent = this.onFieldEvent.bind(this);
    this.handlePaymentMethodSave = this.handlePaymentMethodSave.bind(this);
    this.initializeBraintree = this.initializeBraintree.bind(this);
    this.handlePaypalAuth = this.handlePaypalAuth.bind(this);
    this.handlePaypalCancel = this.handlePaypalCancel.bind(this);
    this.onPaymentMethodReceived = this.onPaymentMethodReceived.bind(this);
    this.onSubmit = this.onSubmit.bind(this);

    this.state = {
      braintreeInitialized: false,
      paypalNonce: null,
      paypalEmail: null,
      cardValues: {
        card: null,
        number: '',
        expirationMonth: '',
        expirationYear: '',
        cvv: '',
        postalCode: '',
      },
    };
  }

  componentDidMount() {
    const { braintreeToken, user, submitButton, braintreeClientInstance } = this.props;
    if (braintreeToken && submitButton && braintreeClientInstance) {
      this.initializeBraintree();
    }

    if (!user || !user.isLoggedIn()) { return; }
    const paymentDetailOffset = get(document.getElementById('payment-details'), 'offsetTop', 0);
    Scroll.animateScroll.scrollTo(paymentDetailOffset - 10, {
      duration: 500,
      delay: 50,
      smooth: true,
    });
  }

  componentWillUnmount() {
    this.setState({ braintreeInitialized: false });
    if (get(this.props.submitButton, 'removeEventListener')) {
      this.props.submitButton.removeEventListener('click', this.onSubmit);
    }
  }

  componentDidUpdate(prevProps) {
    const { braintreeToken, submitButton, isPaypal, braintreeClientInstance, paypalInstance } = this.props;

    if (
      (!this.state.braintreeInitialized || (prevProps.isPaypal && !isPaypal)) &&
      braintreeClientInstance &&
      paypalInstance &&
      braintreeToken &&
      get(submitButton, 'addEventListener', false)) {
      this.initializeBraintree();
    }
  }

  onPaymentMethodReceived(paymentMethodNonce) {
    const { deviceData } = this.props.dataCollector || {};
    this.props.submitCheckout({ paymentMethodNonce, deviceData });
  }

  onFieldEvent(e) {
    const dataFieldName = e.target.getAttribute('data-field-name');
    let fieldValidity;
    const cvvLength = (this.state.cardValues.card && this.state.cardValues.card.code.size) || 4;
    if (dataFieldName === 'cvv') {
      fieldValidity = valid.cvv(e.target.value, cvvLength);
    } else {
      fieldValidity = (valid[dataFieldName] ? valid[dataFieldName](e.target.value) : null);
    }
    if (!fieldValidity) { return; }

    const cardValues = this.state.cardValues;
    cardValues[dataFieldName] = e.target.value;
    if (dataFieldName === 'number') {
      cardValues.card = fieldValidity.card;
    }
    this.setState({ cardValues });

    Object.assign(fieldValidity, {
      isEmpty: !e.target.value.length,
      target: {
        fieldKey: dataFieldName,
      },
    });

    // pass in CVV because if the number changes we need to re-validate the CVV even if it hasn't changed
    this.props.braintreeFieldStateChange({ event: fieldValidity, cvv: this.state.cardValues.cvv });
  }

  onSubmit() {
    const { isPaypal, checkoutTotal, selectedPaymentMethod } = this.props;

    // If you want to check for empty fields use generateRequiredFields() here.

    if (isPaypal) {
      this.onPaymentMethodReceived(this.state.paypalNonce);
    } else if (checkoutTotal > 0 && !selectedPaymentMethod) {
      this.props.braintreeClientInstance.request({
        endpoint: 'payment_methods/credit_cards',
        method: 'post',
        data: {
          creditCard: {
            number: this.state.cardValues.number,
            cvv: this.state.cardValues.cvv,
            expirationMonth: this.state.cardValues.expirationMonth,
            expirationYear: this.state.cardValues.expirationYear,
            billingAddress: {
              postalCode: this.state.cardValues.postalCode,
            },
            options: {
              validate: true,
            },
          },
        },
      }, (requestErr, response) => {
        // More detailed example of handling API errors: https://codepen.io/braintree/pen/MbwjdM
        // Since this doesnt go to checkout saga, error tooltips for vehice size, firstname, etc dont display.
        if (requestErr) {
          const fieldErrors = get(requestErr, ['details', 'originalError', 'fieldErrors', 0, 'fieldErrors']);
          this.props.handlePaymentError(fieldErrors);
          this.props.addMessageAndScrollToTop(paymentTokenizationFailureMessage);
        } else {
          this.onPaymentMethodReceived(response.creditCards[0].nonce);
        }
      });
    }
  }

  initializeBraintree() {
    const { paypalInstance, submitButton } = this.props;
    const paypalButton = document.getElementById('paypal-button');
    this.setState({ braintreeInitialized: true });

    submitButton.addEventListener('click', this.onSubmit);
    paypalButton.addEventListener('click', () => {
      // Because tokenization opens a popup, this has to be called as a result of
      // customer action, like clicking a button. You cannot call this at any time.
      paypalInstance.tokenize({
        flow: 'vault',
        // For more tokenization options, see the full PayPal tokenization documentation
        // http://braintree.github.io/braintree-web/current/PayPal.html#tokenize
      }, (tokenizeErr, payload) => {
        if (tokenizeErr) {
          if (tokenizeErr.type !== 'CUSTOMER') {
            console.error('Error tokenizing:', tokenizeErr);
          }
          return;
        }

        // Tokenization succeeded
        // paypalButton.setAttribute('disabled', true);
        this.handlePaypalAuth(payload);
      });
    }, false);
  }

  handlePaypalAuth({ nonce, details }) {
    this.props.changePaymentMethod({
      isPaypal: true,
      newPayment: true,
      saveCreditCard: false,
    });

    // Remove other braintree fields from form validation
    this.setState({ paypalNonce: nonce, paypalEmail: details.email });
    this.props.modifyRequiredFieldList({ remove: BRAINTREE_FIELDS });
  }

  handlePaypalCancel() {
    this.props.changePaymentMethod({
      isPaypal: false,
      newPayment: true,
      saveCreditCard: true,
    });

    this.setState({ papyalNonce: null, paypalEmail: null });
    // Re-add other braintree fields to form validation
    this.props.modifyRequiredFieldList({ add: BRAINTREE_FIELDS });
  }

  handlePaymentMethodSave() {
    this.props.toggleSavePayment();
  }

  render() {
    const {
      newPaymentMethod,
      isPaypal,
      saveCreditCard,
      requiredFields,
      requiredFieldErrors,
      shouldScrollToError,
      modifyRequiredFieldList,
      trackEvent,
    } = this.props;

    const creditCardFieldsClassNames = cx({
      'non-paypal': true,
      'hidden': this.props.isPaypal,
    });

    return (
      <PaymentMethodForm
        newPaymentMethod={newPaymentMethod}
        isPaypal={isPaypal}
        paypalEmail={this.state.paypalEmail}
        creditCardFieldsClassNames={creditCardFieldsClassNames}
        onFieldEvent={this.onFieldEvent}
        saveCreditCard={saveCreditCard}
        handlePaymentMethodSave={this.handlePaymentMethodSave}
        requiredFields={requiredFields}
        requiredFieldErrors={requiredFieldErrors}
        shouldScrollToError={shouldScrollToError}
        setShouldScrollToError={this.props.setShouldScrollToError}
        modifyRequiredFieldList={modifyRequiredFieldList}
        hideSaveToggle={false}
        handlePaypalCancel={this.handlePaypalCancel}
        trackEvent={trackEvent}
      />
    );
  }
}

NewPaymentMethod.propTypes = propTypes;
NewPaymentMethod.defaultProps = defaultProps;

const mapStateToProps = (state) => {
  const {
    braintreeToken,
  } = state.paymentMethods;

  const {
    shouldScrollToError,
    isPaypal,
    saveCreditCard,
    checkoutTotal,
  } = state.checkout;

  const {
    requiredFields,
    requiredFieldErrors,
    newPaymentMethod,
    selectedPaymentMethod,
  } = state.paymentMethods;

  return {
    braintreeToken,
    requiredFields,
    requiredFieldErrors,
    shouldScrollToError,
    isPaypal,
    saveCreditCard,
    newPaymentMethod,
    selectedPaymentMethod,
    checkoutTotal,
  };
};

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    modifyRequiredFieldList: modifyRequiredFieldListCreator,
    setShouldScrollToError,
    braintreeFieldStateChange,
    changePaymentMethod,
    handlePaymentError,
    toggleSavePayment,
    addMessageAndScrollToTop,
    trackEvent: trackEventCreator,
  }, dispatch)
);

export default connect(mapStateToProps, mapDispatchToProps)(NewPaymentMethod);
